import { useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { Session } from '@app/api';
import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import {
  NewFeatureBanner,
  Button,
  Dropdown,
  OverlayLoader,
  PageTitle,
  SpecialOfferBigBlock,
} from 'components';
import { MainDashboardPage } from 'components/PageLayout/MainDashboardPage';
import { DashboardListContentPage } from 'components/PageLayout/DashboardListContentPage';

import { SessionItem } from './components/SessionItem';
import { EmptyList } from './EmptyList';
import { useCurrentOrganizationId, useOrganizationSessions } from 'queries';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  titleLine: {
    display: 'flex',
    marginBottom: theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h3,
    flex: 1,
  },
  sessionItem: {
    marginTop: theme.marginBase,
  },
}));

export const SessionsList = () => {
  const theme: Theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles({ theme });

  const [filter, setFilter] = useState('active');
  const orgId = useCurrentOrganizationId();
  const sessionsRes = useOrganizationSessions(orgId, filter);
  const { data, isFetching } = sessionsRes;

  const sessionOptions = useMemo(
    () => [
      { label: t('sessions.list.archivedSection.active'), value: 'active' },
      {
        label: t('sessions.list.archivedSection.archived'),
        value: 'archived',
      },
      { label: t('sessions.list.archivedSection.all'), value: 'all' },
    ],
    [t],
  );

  const total =
    data && data.pages.length
      ? data.pages[data.pages.length - 1].pagination.count
      : 0;

  return (
    <AnalyticsCategoryProvider category={Categories.GENERAL}>
      <MainDashboardPage>
        <NewFeatureBanner />

        <PageTitle title={t('sessions.list.metaTitle')} />
        <DashboardListContentPage
          title={t('sessions.list.title')}
          listEmpty={!total}
          fetching={isFetching}
          emptyListContent={<EmptyList />}
          actions={
            <Button
              to="/sessions/create"
              text={t('sessions.list.create')}
              small
              trackingName="New Session"
              color="accent"
            />
          }
          titleSideElement={
            <Dropdown
              options={sessionOptions}
              value={filter}
              onChange={setFilter}
            />
          }
          infiniteQueryResponse={sessionsRes}
          loader={<SessionItem className={classes.sessionItem} />}
          key={orgId}
        >
          {isFetching && !total && <OverlayLoader transparent />}
          {!isFetching && !total && <EmptyList />}
          {!!total && <SpecialOfferBigBlock />}
          {data &&
            data.pages.map((page) =>
              page.data.map((session: Session) => (
                <SessionItem
                  key={session.id}
                  session={session}
                  className={classes.sessionItem}
                  to={`/sessions/details/${session.id}`}
                />
              )),
            )}
        </DashboardListContentPage>
      </MainDashboardPage>
    </AnalyticsCategoryProvider>
  );
};
